// Eskom PARCC — the client register reads and their row caps (29 Sep 2026).
//
// Every whole-population connector query (`Pull*` in dataconnect/connector/queries.gql)
// carries a fixed `limit`. Data Connect returns the first N rows in table order and says
// NOTHING when more exist — so a population that outgrows its cap is silently truncated.
// That is not a display bug: an admin's editor seeded from a truncated `PullFindings`
// (capped at 5,000 while 7,521 lived) had `reconcileFindingsForTx` close every unseen
// open line as a buyer fix and delete every unseen note — 327 lines and 334 notes on
// ~50 transactions between 10 and 29 Sep 2026 (see the journal, 2026-09-29).
//
// This module is the ONE place the caps are known on the client. `readCaps.test.ts`
// pins each constant to the literal in queries.gql, so the two cannot drift; the
// fetchers in db.ts pass every result through {@link assertUnderReadCap}, which
// REFUSES a result that lands exactly on its cap — a loud failure the person sees,
// instead of a register that is quietly missing rows and a save that acts on it.
//
// Raising a cap is a connector change (`deploy --only dataconnect:eskom-parcc-service:default`,
// the 29 Sep route) plus the matching constant here; the test fails until both move.

/** Row caps of the whole-population client reads, by the connector query that carries them. */
export const CLIENT_READ_CAPS = {
  /** `PullTransactions` (deleted = false). 981 live on 29 Sep 2026. */
  transactions: 10000,
  /** `PullTransactionsForManage` (all rows, incl. removed). 2,509 on 29 Sep 2026. */
  transactionsForManage: 10000,
  /** `PullFindings` (deleted = false). 7,521 live on 29 Sep 2026 — was capped at 5,000 until then. */
  findings: 20000,
  /** `PullWorkingPapers` (deleted = false). 874 live on 29 Sep 2026. */
  workingPapers: 10000,
  /** `PullCesRows` (deleted = false). 20,730 live on 29 Sep 2026 — OVER the old cap of 20,000. */
  cesRows: 50000,
} as const;

export type ClientReadName = keyof typeof CLIENT_READ_CAPS;

/** Thrown when a population read returns exactly its cap — the result cannot be trusted. */
export class ReadCapError extends Error {
  readonly readName: ClientReadName;
  readonly cap: number;
  readonly rows: number;
  constructor(readName: ClientReadName, rows: number, cap: number) {
    super(
      `The ${readName} read returned ${rows.toLocaleString('en-GB')} rows — its query cap of ` +
        `${cap.toLocaleString('en-GB')}. More may exist, so the register would be incomplete and a ` +
        `save could act on rows it cannot see; nothing was loaded. Raise the limit on the ` +
        `connector query and redeploy it.`,
    );
    this.name = 'ReadCapError';
    this.readName = readName;
    this.cap = cap;
    this.rows = rows;
  }
}

/**
 * Pass a population read through unchanged when it is safely under its cap; throw a
 * {@link ReadCapError} when it sits AT the cap (the only signal Data Connect gives that
 * rows were cut). Pure; the fetchers in db.ts call it on every whole-population pull.
 */
export function assertUnderReadCap<T>(readName: ClientReadName, rows: T[]): T[] {
  const cap = CLIENT_READ_CAPS[readName];
  if (rows.length >= cap) throw new ReadCapError(readName, rows.length, cap);
  return rows;
}
